import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { envVars } from '../../../constants/envVars'
import {
  CheckSlotsDTO,
  CheckSlotsResult,
  CreateSessionDTO,
} from '../../../features/manager/schedules-sessions/dto/scheduling.dto'
import {
  AssessmentQuestion,
  AssessmentStatus,
  AssessmentValue,
  ChatListItem,
  ChatMessages,
  CoachInfo,
  PrivateChatResult,
  Session,
  SessionStats,
  SuggestedDate,
} from '../../../interfaces/main-interfaces'
import {
  TeamMember,
  TeamMemberAssessmentStatus,
  TeamMemberDTO,
  TeamMembersParams,
} from '../../../lib/widgets/team-members/TeamMembers'
import { RootState } from '../store.config'
import { OnboardingCoach } from '../../../interfaces/coach.interface'
import { AUTH_TAG } from './auth.service'
import {
  UserInfo,
  UserProfile,
} from '../../../interfaces/entities/auth.entities'

export interface ChatsResult {
  data: PrivateChatResult[]
  unreadMessagesCount: number
}

const TAG = {
  SESSIONS: 'SESSIONS',
  BLOCKED_SLOTS: 'BLOCKED_SLOTS',
  TEAM_MEMBERS: 'TEAM_MEMBERS',
  CHAT: 'CHAT',
  ASSESSMENT: 'ASSESSMENT',
}

export const managerService = createApi({
  reducerPath: 'managerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: envVars.BASE_URL,
    prepareHeaders: (headers, api) => {
      const store = api.getState() as RootState
      if (store.auth.user) {
        headers.set('Authorization', `Bearer ${store.auth.user.token}`)
      }
      return headers
    },
  }),

  tagTypes: [
    TAG.SESSIONS,
    TAG.BLOCKED_SLOTS,
    TAG.TEAM_MEMBERS,
    TAG.CHAT,
    TAG.ASSESSMENT,
    AUTH_TAG.USER,
  ],

  endpoints: (builder) => ({
    checkSlots: builder.query({
      query: (params) => ({
        url: 'manager/check-slot',
        params,
      }),
      providesTags: [TAG.BLOCKED_SLOTS],
    }),

    getSessions: builder.query<
      { data: Session[]; defaultTitle: string; defaultIndividualTitle: string },
      { start: string; end?: string }
    >({
      query: (params) => ({
        url: 'user/sessions',
        params,
      }),
      providesTags: [TAG.SESSIONS],
    }),
    getSessionStats: builder.query<SessionStats, void>({
      query: () => ({
        url: 'user/session-stats',
      }),
      providesTags: [TAG.SESSIONS],
    }),

    updateTimezone: builder.mutation<{ ok: boolean }, { timezone: string }>({
      query: (body) => ({ body, url: 'user/timezone', method: 'POST' }),
      invalidatesTags: [AUTH_TAG.USER],
    }),

    updateProfile: builder.mutation<UserInfo, UserProfile>({
      query: (body) => ({ body, url: 'user/profile', method: 'POST' }),
      invalidatesTags: [AUTH_TAG.USER],
    }),

    getChats: builder.query<ChatsResult, unknown>({
      query: () => ({
        url: `user/chat`,
      }),
      providesTags: [TAG.CHAT],
    }),
    getChat: builder.query<ChatMessages, string>({
      query: (id) => ({
        url: `user/chat/${id}`,
      }),
      providesTags: [TAG.CHAT],
    }),
    createChat: builder.mutation<{ data: ChatListItem }, { memberId: string }>({
      query: (body) => ({
        url: 'user/chat',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TAG.CHAT],
    }),

    createSession: builder.mutation<unknown, CreateSessionDTO>({
      query: (body) => ({
        url: 'manager/book',
        body,
        method: 'POST',
      }),
      invalidatesTags: [TAG.SESSIONS],
    }),

    updateSession: builder.mutation<
      unknown,
      {
        body: CreateSessionDTO
        id: string
      }
    >({
      query: ({ body, id }) => ({
        url: `manager/book/${id}`,
        body,
        method: 'PATCH',
      }),
      invalidatesTags: [TAG.SESSIONS],
    }),

    deleteSession: builder.mutation<{ message: string; ok: boolean }, string>({
      query: (id) => ({
        url: `manager/book/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TAG.SESSIONS],
    }),

    checkAvailability: builder.query<
      {
        data: {
          free: SuggestedDate[]
          partial: SuggestedDate[]
        }
      },
      CheckSlotsDTO
    >({
      query: (params) => ({
        url: 'manager/slots',
        params,
        method: 'GET',
      }),
    }),

    getTeamMembers: builder.query<TeamMember[], TeamMembersParams>({
      query: (params) => ({
        url: 'manager/team',
        params,
      }),
      providesTags: [TAG.TEAM_MEMBERS],
    }),

    inviteTeamMember: builder.mutation<unknown, TeamMemberDTO>({
      query: (body) => ({
        url: 'manager/team',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TAG.TEAM_MEMBERS],
    }),

    getTeamAssessmentStatus: builder.query<TeamMemberAssessmentStatus[], void>({
      query: () => ({
        url: 'manager/team/assessment-status',
      }),
      providesTags: [TAG.ASSESSMENT, TAG.TEAM_MEMBERS],
    }),

    sendNudge: builder.mutation<void, { email: string }>({
      query: (body) => ({
        url: 'manager/team/nudge',
        method: 'POST',
        body,
      }),
    }),

    getCoachInfo: builder.query<CoachInfo, any>({
      query: (params) => ({
        url: 'user/coachinfo',
        params,
      }),
    }),

    getAssessmentStatus: builder.query<AssessmentStatus, void>({
      query: () => ({
        url: 'user/assessment-status',
      }),
      providesTags: [TAG.ASSESSMENT],
    }),

    getAssessmentQuestions: builder.query<
      { questions: AssessmentQuestion[]; values: AssessmentValue[] },
      void
    >({
      query: () => ({
        url: 'user/assessment',
      }),
    }),

    postAssessmentQuestions: builder.mutation<any, any>({
      query: (body) => ({
        url: 'user/assessment',
        body,
        method: 'POST',
      }),
      invalidatesTags: [TAG.ASSESSMENT],
    }),

    getAssessmentValueScores: builder.query<
      {
        scores: { self: Record<string, number>; team: Record<string, number> }
        values: Record<string, string>
        answers: any[]
      },
      void
    >({
      query: () => ({
        url: 'user/assessment/value-scores',
        method: 'GET',
      }),
      providesTags: [TAG.ASSESSMENT],
    }),

    getHighlightedAreas: builder.query({
      query: (params) => ({
        url: 'user/assessment/highlighted-areas',
        params,
      }),
      providesTags: [TAG.ASSESSMENT],
    }),

    getAssessmentAnswers: builder.query<any, void>({
      query: () => ({
        url: 'user/assessment/answers',
      }),
      providesTags: [TAG.ASSESSMENT],
    }),

    getCoachOptions: builder.query<{ data: OnboardingCoach[] }, void>({
      query: () => ({
        url: 'manager/coach/options',
      }),
    }),

    selectCoach: builder.mutation<{ ok: boolean }, any>({
      query: (body) => ({
        url: 'manager/coach/options',
        body,
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useCheckSlotsQuery,
  useCreateSessionMutation,
  useLazyCheckAvailabilityQuery,
  useGetSessionsQuery,
  useGetSessionStatsQuery,
  useGetChatQuery,
  useCreateChatMutation,
  useDeleteSessionMutation,
  useUpdateSessionMutation,
  useGetTeamMembersQuery,
  useInviteTeamMemberMutation,
  useGetChatsQuery,
  useGetCoachInfoQuery,
  useUpdateTimezoneMutation,
  useUpdateProfileMutation,
  useGetAssessmentQuestionsQuery,
  usePostAssessmentQuestionsMutation,
  useGetCoachOptionsQuery,
  useSelectCoachMutation,
  useGetAssessmentStatusQuery,
  useGetTeamAssessmentStatusQuery,
  useSendNudgeMutation,
  useGetAssessmentValueScoresQuery,
  useGetHighlightedAreasQuery,
  useGetAssessmentAnswersQuery,
} = managerService
